/**
 * #13 送禮備妥確認頁(2026-05-26 channel-less 重做) — LIFF /gift/sent/:code
 *
 * sender 從 GiftWizard 送出後跳來。顯示:
 *   - 私享連結(LIFF /gift/receive/:short_code)+ QR Code(掃描或列印用)
 *   - 三個 CTA:
 *     • 複製連結
 *     • LINE 分享(shareTargetPicker 推 Flex 卡 + 連結)
 *     • 看祝福卡預覽(在新分頁開 receive 頁面 sender 自己 review)
 *   - 禮物摘要(商品 + 金額,sender 自己看的)
 *   - 收禮對象備註(sender 填的「給張小姐」,自己看的)
 *   - 收禮狀態(若 recipient 已 link 顯示「已關聯」;若已領取顯示「已領取」)
 *
 * URL param :code 可吃 short_code (6 字元) 或 gift_id (12 hex,backward compat)。
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Gift as GiftIcon } from "lucide-react";
import StatusMark from "../components/StatusMark";
import { formatGiftLines, getGift, type Gift, GIFT_TEMPLATES, GIFT_STATUS_LABELS } from "../api/gifts";
import { liffSdk } from "../liff";
import { useUIStore } from "../store/uiStore";


export default function GiftSent() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState<Gift | null>(null);
  const [loading, setLoading] = useState(true);
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    (async () => {
      try {
        const r = await getGift(id);
        setGift(r.gift);
      } catch {
        showToast("載入禮物失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, showToast]);

  // share URL — 用 short_code(新版)優先,fallback 到 gift_id(舊版)
  const liffId = (import.meta.env.VITE_LIFF_ID as string) || "";
  const shareCode = gift?.short_code || gift?.gift_id || "";
  const shareUrl = useMemo(
    () => `https://liff.line.me/${liffId}/gift/receive/${shareCode}`,
    [liffId, shareCode],
  );

  // QR Code — 用 qrserver 公開 API 產 SVG,200x200,容錯率 Q
  // 隱私:URL 是 share link 本身(本來就是要散布),沒個人資料,可放外部 service
  const qrUrl = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=320x320&ecc=Q&format=svg&data=${encodeURIComponent(shareUrl)}`,
    [shareUrl],
  );

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }
  if (!gift) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4 text-muted">
        <div>找不到禮物</div>
        <button onClick={() => navigate("/home")} className="btn-primary">回首頁</button>
      </div>
    );
  }

  const tpl = GIFT_TEMPLATES.find((t) => t.key === gift.blessing_card.template);
  const message = gift.blessing_card.message || tpl?.defaultMessage || "";

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast("連結已複製", "success");
    } catch {
      showToast("複製失敗,請手動長按連結", "error");
    }
  }

  async function shareToLineFriend() {
    if (!gift) return;
    const liff = liffSdk();
    try {
      if (!liff.isApiAvailable("shareTargetPicker")) {
        showToast("此裝置不支援 LINE 分享,可改用複製連結", "error");
        return;
      }
      const senderName = gift.sender_name || "霽雲會員";
      const flexMessage = {
        type: "flex" as const,
        altText: `霽雲・送禮分享 — ${senderName} 為您獻上一份禮物`,
        contents: {
          type: "bubble" as const,
          size: "kilo" as const,
          hero: {
            type: "image" as const,
            url: "https://storage.googleapis.com/yannwo-product-images/product-liu-bai.jpg",
            size: "full" as const,
            aspectRatio: "20:13" as const,
            aspectMode: "cover" as const,
          },
          header: {
            type: "box" as const,
            layout: "vertical" as const,
            backgroundColor: "#00382C",
            paddingAll: "20px",
            contents: [
              { type: "text" as const, text: "霽雲・送禮分享", size: "sm", color: "#F3F0E9", align: "center" as const },
              { type: "text" as const, text: "為您獻上一份禮物", size: "xl", weight: "bold" as const, color: "#FFFFFF", align: "center" as const, wrap: true },
            ],
          },
          body: {
            type: "box" as const,
            layout: "vertical" as const,
            spacing: "md" as const,
            paddingAll: "18px",
            contents: [
              { type: "text" as const, text: `來自:${senderName}`, size: "sm", color: "#1D252D", align: "center" as const, wrap: true },
              { type: "separator" as const, color: "#C7D8CD" },
              { type: "text" as const, text: message.slice(0, 120), size: "md", color: "#1D252D", align: "center" as const, wrap: true },
              { type: "separator" as const, color: "#C7D8CD" },
              { type: "text" as const, text: "點開下方按鈕,展開您的私享禮盒", size: "xs", color: "#6FA98F", align: "center" as const, wrap: true },
            ],
          },
          footer: {
            type: "box" as const,
            layout: "vertical" as const,
            paddingAll: "12px",
            contents: [
              {
                type: "button" as const,
                style: "primary" as const,
                color: "#005844",
                action: {
                  type: "uri" as const,
                  label: "打開禮盒",
                  uri: shareUrl,
                },
                height: "sm" as const,
              },
            ],
          },
        },
      };

      const result = await liff.shareTargetPicker([flexMessage] as any);
      if (result) {
        showToast("已分享", "success");
      } else {
        showToast("已取消分享", "info");
      }
    } catch (e: any) {
      console.warn("[shareTargetPicker] err", e);
      showToast("分享未成功,請稍後再試", "error");
    }
  }

  function openPreview() {
    // sender 自己預覽 — 開新 tab 看 receive 頁面(會走 preview 路徑,因為 sender ≠ recipient_member_uid)
    window.open(shareUrl, "_blank");
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3">
        <button onClick={() => navigate("/home")} className="text-white text-xl" aria-label="返回">‹</button>
        <h1 className="text-xl font-bold">禮物已備妥</h1>
      </header>

      <div className="px-5 mt-5 space-y-4">
        {/* 大圖示確認 */}
        <div className="text-center py-4">
          <StatusMark icon={GiftIcon} className="mb-3" />
          <div className="text-lg font-bold text-primary">您的私享禮物已備妥</div>
          <div className="text-xs text-muted mt-1">
            禮物編號 #{(gift.short_code || gift.gift_id).slice(0, 8)} · {GIFT_STATUS_LABELS[gift.status]}
          </div>
        </div>

        {/* QR + 連結 — 核心區塊 */}
        <div className="card bg-white text-center space-y-3 p-5">
          <div className="text-sm font-bold text-primary">私享禮物連結</div>
          <div className="text-xs text-muted">
            收禮人掃 QR 或點連結 → 看祝福 → 接受禮物
          </div>

          <div className="flex justify-center py-2">
            <img
              src={qrUrl}
              alt="禮物連結 QR Code"
              className="w-48 h-48"
              loading="lazy"
            />
          </div>

          <div className="bg-cream rounded p-2 break-all text-xs text-ink font-mono leading-relaxed">
            {shareUrl}
          </div>

          <button onClick={shareToLineFriend} className="btn-primary w-full py-3 text-base">
            用 LINE 分享禮物
          </button>
          <div className="text-xxs text-muted">
            推薦用 LINE 分享 — 對方會收到漂亮的霽雲禮物卡,而非陽春連結
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={copyLink} className="btn-secondary text-xs py-2">
              複製連結
            </button>
            <button onClick={openPreview} className="btn-secondary text-xs py-2">
              預覽
            </button>
          </div>
        </div>

        {/* 收禮狀態(已 link / 已領取) */}
        {gift.recipient_is_member && (
          <div className="card bg-sage-light/20 text-sm">
            <div className="text-primary font-bold">
              <Check size={14} strokeWidth={2} className="inline -mt-0.5 mr-1" aria-hidden="true" />已關聯霽雲會員
            </div>
            <div className="text-xs text-muted mt-1">
              收禮人為霽雲會員,瓶數將於對方領取後自動歸入其燕窩存摺。
            </div>
          </div>
        )}
        {gift.status === "received" && gift.received_at && (
          <div className="card bg-primary/10 text-sm">
            <div className="text-primary font-bold">
              <Check size={14} strokeWidth={2} className="inline -mt-0.5 mr-1" aria-hidden="true" />對方已領取
            </div>
            <div className="text-xs text-muted mt-1">
              領取時間:{new Date(gift.received_at).toLocaleString("zh-TW")}
            </div>
          </div>
        )}

        {/* 禮物摘要(sender 自己看) */}
        <div className="card">
          <div className="text-sm text-muted mb-3 font-medium">禮物內容</div>
          <div className="space-y-2 text-sm">
            {gift.items.map((it) => (
              <div key={it.product_id} className="flex justify-between">
                <span className="flex-1">{it.name} <span className="text-muted">× {it.qty}</span></span>
                {typeof it.subtotal === "number" && (
                  <span className="text-ink">NT$ {it.subtotal.toLocaleString()}</span>
                )}
              </div>
            ))}
          </div>
          {typeof gift.total === "number" && (
            <div className="border-t border-line mt-3 pt-3 flex justify-between">
              <span className="text-muted">合計</span>
              <span className="font-bold text-ink">NT$ {gift.total.toLocaleString()}</span>
            </div>
          )}
          <div className="text-xs text-muted mt-1 text-right">
            折算 {formatGiftLines(gift)}
          </div>
        </div>

        {/* 收禮對象備註(sender 自己看的標記) */}
        {gift.recipient_name && (
          <div className="card">
            <div className="text-sm text-muted mb-1 font-medium">給誰?(您的備註)</div>
            <div className="text-ink">{gift.recipient_name}</div>
          </div>
        )}

        {/* 祝福卡預覽 */}
        <div className="card bg-gradient-to-br from-cream to-sage-light/30 p-4 text-center space-y-2">
          <div className="text-xxs text-muted">祝福卡 · {tpl?.label}</div>
          <div className="text-ink text-sm leading-relaxed whitespace-pre-wrap break-words py-2">
            {message}
          </div>
        </div>

        <div className="text-xs text-muted bg-cream p-3 rounded-lg leading-relaxed">
          本送禮為模擬下單(資料已記錄),金流接通後將通知您完成付款。
          付款完成後,瓶數將自動由您的存摺撥入收禮人帳戶(若為會員)。
        </div>

        <button onClick={() => navigate("/home")} className="btn-secondary w-full">
          返回會員首頁
        </button>
      </div>
    </div>
  );
}
